import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { IMAGES } from "../data/content";
import { ArrowRight, Brain, Sparkles, Layers } from "lucide-react";

export default function Approccio() {
  useSeo(
    "Approccio: Psicoterapia Cognitivo-Comportamentale ed EMDR a Macerata",
    "L'approccio terapeutico della Dott.ssa Susanna Paoletti a Macerata: psicoterapia cognitivo-comportamentale ed EMDR, con interventi valutati individualmente.",
    "/approccio"
  );

  return (
    <>
      <PageHeader
        eyebrow="Approccio terapeutico"
        title="Un metodo attento, mai uguale a se stesso"
        subtitle="Nessun approccio è infallibile o adatto a chiunque. Ogni intervento viene valutato individualmente e concordato durante i colloqui."
      />

      {/* CBT */}
      <section className="py-20 md:py-28" data-testid="approccio-cbt">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3 text-sage-dark mb-6">
                <Brain size={22} />
                <span className="text-xs uppercase tracking-[0.28em]">01 — Cognitivo-comportamentale</span>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif text-4xl md:text-6xl text-ink tracking-tight leading-tight">
                Pensieri, emozioni e comportamenti
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-6 space-y-5 text-lg text-stone leading-relaxed">
                <p>
                  La psicoterapia cognitivo-comportamentale è un percorso orientato alla comprensione del
                  rapporto tra ciò che pensiamo, ciò che proviamo e ciò che facciamo.
                </p>
                <p>
                  Insieme osserviamo gli schemi che si ripetono e che a volte alimentano il disagio, per poi
                  sperimentare, con gradualità, modalità di pensiero e di azione più funzionali al benessere.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-2xl border border-border">
                <img
                  src={IMAGES.softLight}
                  alt="Luce naturale che filtra da una finestra, atmosfera calma"
                  className="w-full h-[360px] md:h-[460px] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EMDR */}
      <section id="emdr" className="scroll-mt-24 py-20 md:py-28 bg-ink text-cream relative overflow-hidden" data-testid="approccio-emdr">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 lg:gap-16 items-center relative z-10">
          <div className="md:col-span-6 order-2 md:order-none">
            <Reveal>
              <div className="overflow-hidden rounded-2xl border border-cream/15">
                <img
                  src={IMAGES.natureTexture}
                  alt="Foglie di eucalipto sfocate, tonalità verdi delicate"
                  className="w-full h-[360px] md:h-[460px] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3 text-sage-light mb-6">
                <Sparkles size={22} />
                <span className="text-xs uppercase tracking-[0.28em]">02 — Terapia EMDR</span>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-tight">
                Lavorare sulle esperienze difficili
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-6 space-y-5 text-lg text-cream/75 leading-relaxed">
                <p>
                  L'EMDR è un approccio psicoterapeutico utilizzato da professionisti adeguatamente formati per
                  lavorare su esperienze traumatiche o emotivamente difficili.
                </p>
                <p>
                  Non è una tecnica universale: la sua eventuale proposta nasce dall'ascolto della storia di ciascuno.
                  Ogni intervento viene valutato individualmente e concordato durante i colloqui, nel mio studio a Macerata.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-9">
                <PillButton to="/contatti" variant="outline" className="border-cream/30 text-cream hover:bg-cream hover:text-ink" testid="approccio-cta">
                  Parlane con me <ArrowRight size={18} />
                </PillButton>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="pointer-events-none absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-sage/20 blur-3xl" />
      </section>

      {/* 03 — Approccio integrato */}
      <section className="py-20 md:py-28" data-testid="approccio-integrato">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 text-sage-dark mb-6">
                <Layers size={22} />
                <span className="text-xs uppercase tracking-[0.28em]">03 — Un approccio integrato e personalizzato</span>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif text-4xl md:text-6xl text-ink tracking-tight leading-tight">
                Un unico percorso, cucito su di te
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <div className="space-y-5 text-lg text-stone leading-relaxed">
                <p>
                  L'approccio cognitivo-comportamentale e l'EMDR rappresentano, nella mia pratica, strumenti che
                  possono integrarsi all'interno di un unico percorso terapeutico.
                </p>
                <p>
                  In base alla valutazione e agli obiettivi concordati, posso integrare differenti modalità di
                  intervento, scegliendo quelle più appropriate per affrontare le difficoltà presenti, favorire una
                  maggiore consapevolezza e sostenere il cambiamento.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Conclusione */}
      <section className="pb-24 md:pb-36" data-testid="approccio-conclusione">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-sand border border-border rounded-2xl p-10 md:p-16 text-center">
              <p className="font-serif text-2xl md:text-4xl text-ink leading-snug tracking-tight text-balance">
                La psicoterapia diventa così uno spazio nel quale sentirsi ascoltati e accolti, comprendere più
                profondamente ciò che sta accadendo, sviluppare nuove risorse e costruire, passo dopo passo, modalità
                più efficaci e consapevoli per affrontare la propria vita.
              </p>
              <div className="mt-10 flex justify-center">
                <PillButton to="/contatti" testid="approccio-conclusione-cta">
                  Richiedi un primo colloquio <ArrowRight size={18} />
                </PillButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
